/**
 * Client-side IPC commercial preview — mirrors backend computeIpcCommercialAmounts.
 * Authority remains on the server at create/patch; this is for live UI totals only.
 */

export type IpcValuationPreviewInput = {
  grossApplied: number;
  advanceRecoveryRatePct?: number;
  advanceRecoveryAmount?: number;
  remainingAdvanceBalance?: number;
  retentionRatePct?: number;
  retentionWithheldAmount?: number;
  retentionReleasedAmount?: number;
  retentionCap?: number;
};

export type IpcValuationPreview = {
  grossApplied: number;
  advanceRecovery: number;
  retentionWithheld: number;
  retentionReleased: number;
  netApplied: number;
};

function round2(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

export function previewIpcValuation(input: IpcValuationPreviewInput): IpcValuationPreview {
  const gross = round2(Math.max(0, input.grossApplied || 0));

  let advance = 0;
  if (input.advanceRecoveryAmount != null) {
    advance = round2(Math.max(0, input.advanceRecoveryAmount));
  } else if (input.advanceRecoveryRatePct != null) {
    advance = round2((gross * input.advanceRecoveryRatePct) / 100);
  }
  if (input.remainingAdvanceBalance != null) {
    advance = round2(Math.min(advance, Math.max(0, input.remainingAdvanceBalance)));
  }

  let retention = 0;
  if (input.retentionWithheldAmount != null) {
    retention = round2(Math.max(0, input.retentionWithheldAmount));
  } else if (input.retentionRatePct != null) {
    retention = round2((gross * input.retentionRatePct) / 100);
  }
  if (input.retentionCap != null) {
    retention = round2(Math.min(retention, Math.max(0, input.retentionCap)));
  }

  const release = round2(Math.max(0, input.retentionReleasedAmount || 0));
  const net = round2(gross - advance - retention + release);

  return {
    grossApplied: gross,
    advanceRecovery: advance,
    retentionWithheld: retention,
    retentionReleased: release,
    netApplied: net,
  };
}
